import React, { useEffect, useReducer, useState } from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import {
  Divider as MuiDivider,
  Typography, Box
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import { useSelector } from "../../../redux/store";
import { useDispatch } from "react-redux";
import { IUser } from "../../../models/User";
import { getUser, getUserProfile } from "../../../selectors";
import { fetchUser } from "../../../redux/actions/userActions";
import ImagesTableHead from "./components/table/ImagesTableHead";
import ImagesTableRow from "./components/table/ImagesTableRow";
import SearchBlock from "./components/SearchBlock";
import { fetchA11yStandard } from "../../../redux/actions/a11yStandardActions";
import TabsBuilding from "./components/TabsBuilding";
import {
  deleteUnitImagesByCompanyId,
  fetchUnitImagesByCompanyId,
  updateUnitImage
} from "../../../redux/actions/unitImagesActions";
import { useParams } from "react-router-dom";
import { fetchUnits } from "../../../redux/actions/unitsActions";
import { AppStateType } from "../../../redux/reducers";
import { fetchA11yesQuery } from "../../../services/a11yesService";
import { Accessibility } from "../../../models/Accessibility";
import LoadingBlock from "./components/LoadingBlock";
import AlertUnitImagesEmpty from "./components/AlertUnitImagesEmpty";
import { useTranslation } from "react-i18next";
import { fetchPropertiesQuery } from "../../../services/propertyService";
import { getComparator, sortList } from "./utils";
import PaginationBlock from "./components/PaginationBlock";
import SelectFilter from "./components/SelectFilter";
import CustomBreadcrumbs from "../../../components/CustomBreadcrumbs";

const Divider = styled(MuiDivider)(spacing);
const TopBlock = styled(Box)`
  display: flex;
  align-items: center;
`

const ImagesPage = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const params: {id: string} = useParams()
  const user = useSelector(getUser);
  const userProfile = useSelector<IUser | undefined>(getUserProfile);
  const unitImagesByCompanyId = useSelector(
    (state) => state.unitImagesReducer.unitImagesByCompanyId
  )
  const a11yStandard = useSelector((state) => state.a11yStandardReducer.a11yStandard)
  const units = useSelector((state: AppStateType) => state.unitsReducer.units);
  const [properties, setProperties] = useState<any>([])
  const [searchText, setSearchText] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [selectedPropertyIndex, setSelectedPropertyIndex] = useState(0)
  const [accessibilities, setAccessibilities] = useState<Accessibility[]>([])
  const [firstRender, setFirstRender] = useState(true)
  const [filteredUnitImagesByPropertyId, setFilteredUnitImagesByPropertyId] = useState<any[]>([])

  const [order, setOrder] = useState<"desc" | "asc">("asc");
  const [orderBy, setOrderBy] = useState<"description" | "updated_at">("description");
  const [typeOrder, setTypeOrder] = useState<"string" | "date">("string")

  const [page, setPage] = React.useState(1);

  const handleRequestSort = (event: any, field: "description" | "updated_at") => {
    const isAsc = orderBy === field && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
    setTypeOrder(field === "description" ? "string" : "date")
  };

  useEffect(() => {
    if (!user) {
      dispatch(fetchUser(user!.user, user!.token))
    }
    if (a11yStandard.length === 0) {
      dispatch(fetchA11yStandard(user!.token))
    }
    if (unitImagesByCompanyId.length === 0) {
      dispatch(fetchUnitImagesByCompanyId(user!.token, params.id))
    }
  }, [])

  useEffect(() => {
    if (userProfile && params.id) {
      fetchPropertiesQuery(user!.token,
        {
          company: params.id,
          limit: 99999,
        }).then((response) => {
        if (response.data?.results?.length > 0) {
          setProperties(response.data.results)
        }
        setFirstRender(false)
        setIsLoading(false)
      })
    }
  }, [userProfile])

  useEffect(() => {
    if (properties.length > 0) {
      handlerProperties()
    }
  }, [properties])

  useEffect(() => {
    if (!firstRender) {
      handlerSelectedPropertyIndex(0)
    }
  }, [searchText])

  // When we update the unitImagesByCompanyId we update filteredUnitImagesByPropertyId
  useEffect(() => {
    if (!firstRender) {
      const fProperties = filteredProperties()
      const filteredUnitImages = unitImagesByCompanyId
        .filter((unitImage) => unitImage.property === fProperties[selectedPropertyIndex].id)
      setFilteredUnitImagesByPropertyId(filteredUnitImages)
    }
  }, [unitImagesByCompanyId])

  const filteredProperties = () => {
    if (properties?.length > 0) {
      return properties.filter((i: any) => (
        i.name.toLowerCase().trim().includes(searchText.toLowerCase().trim())
      ))
    }
    return []
  }

  const fetchAccessibility = async (propertyId: string) => {
    const accessiblityQuery = await fetchA11yesQuery(
      {
        accessibility_unit__property__id: propertyId,
        limit: 99999
      },
      user!.token
    )
    setAccessibilities(accessiblityQuery.results)
  }

  const handlerFilterImagesByPropertyId = (id: string) => {
    const filteredUnitImages = unitImagesByCompanyId
      .filter((unitImage) => unitImage.property === id)
    setFilteredUnitImagesByPropertyId(filteredUnitImages)
  }

  const handlerProperties = async () => {
    setIsLoading(true)
    const fProperties = filteredProperties()
    handlerFilterImagesByPropertyId(fProperties[selectedPropertyIndex].id)
    await dispatch(fetchUnits(fProperties[selectedPropertyIndex].id, user!.token))
    await fetchAccessibility(fProperties[selectedPropertyIndex].id)
    setIsLoading(false)
  }

  const handlerSelectedPropertyIndex = async (index: number) => {
    const fProperties = filteredProperties()
    if (fProperties.length > 0) {
      setSelectedPropertyIndex(index)
      handlerFilterImagesByPropertyId(fProperties[index].id)
      await dispatch(fetchUnits(fProperties[index].id, user!.token))
      await fetchAccessibility(fProperties[index].id)
      setPage(1)
    }
  }

  const statusMandatoryUnitImage = (image: any) => {
    const fProperties = filteredProperties()
    if (fProperties.length > 0 && fProperties[selectedPropertyIndex]) {
      const foundUnit = units.find((u) => u.id === image.accessibility_unit)
      const foundStandard = a11yStandard.find((i) => i.key === fProperties[selectedPropertyIndex].a11y_standard)
      const foundAccessibilities = accessibilities.find((a) => a.id === image.accessibility)
      if (foundUnit && foundStandard && foundAccessibilities) {
        const foundValuesStandardByUnit = foundStandard.values
          .find((i: any) => i.key === foundUnit.type)

        if (foundValuesStandardByUnit) {
          const foundValuesByAccessibilities = foundValuesStandardByUnit.values
            .find((v: any) => v.key === foundAccessibilities.item)

          if (foundValuesByAccessibilities) {
            const foundValuesByAttribute = foundValuesByAccessibilities.values
              .find((v2: any) => v2.key === foundAccessibilities.attribute)

            if (foundValuesByAttribute) {
              return foundValuesByAttribute.is_mandatory_image
            }
          }
        }
      }
    }
    return false
  }

  const editUnitImage = async (id: string, text: string) => {
    const index = filteredUnitImagesByPropertyId.findIndex((i: any) => i.id === id)
    const data = { description: text }
    await dispatch(updateUnitImage(index, id, data, user!.token))
    await dispatch(fetchUnitImagesByCompanyId(user!.token, params.id))
  }

  const deleteUnitImage = async (id: string) => {
    setIsLoading(true)
    const findIndex = unitImagesByCompanyId.findIndex((i) => i.id === id)
    await dispatch(deleteUnitImagesByCompanyId(id, user!.token, findIndex))
    await dispatch(fetchUnitImagesByCompanyId(user!.token, params.id))
    setIsLoading(false)
    // const filterById = filteredUnitImagesByPropertyId.filter((i) => i.id !== id)
    // setFilteredUnitImagesByPropertyId(filterById)
  }

  return (
    <Box p={12}>
      <Helmet title="Pictures List" />
      <Typography variant="h3" gutterBottom display="inline">
        {t('imagesPage_Pictures_List')}
      </Typography>
      <Box mt={1}>
        <CustomBreadcrumbs
          current={t('imagesPage_Pictures_List')}
          before={[
            {to: '/stats', title: t('statsPage_Statistics')},
            {to: '/stats', title: t('statsPage_Business_Metrics')}
          ]}

        />
      </Box>
      <Divider mb={6} mt={4} />
      <TopBlock mb={5}>
        <SearchBlock getText={(e) => setSearchText(e)} />
        {/*<SelectFilter*/}
        {/*  list={[*/}
        {/*    {name: 'All properties', key: 'all'},*/}
        {/*    {name: 'With pictures', key: 'with_pictures'},*/}
        {/*    {name: 'Without pictures', key: 'without_pictures'},*/}
        {/*  ]}*/}
        {/*  selected={'all'}*/}
        {/*  setSelected={() => {}}*/}
        {/*/>*/}
      </TopBlock>
      <TabsBuilding
        value={selectedPropertyIndex}
        setValue={handlerSelectedPropertyIndex}
        building={filteredProperties()}
      />
      {!isLoading && filteredUnitImagesByPropertyId.length > 0 ? (
        <>
          <table width={'100%'}>
            <ImagesTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <tbody>
            {sortList(filteredUnitImagesByPropertyId, getComparator(order, orderBy, typeOrder))
              .slice((page - 1) * 8, (page - 1) * 8 + 8)
              .map((i: any, index: number) => {
                return (
                  <ImagesTableRow
                    id={i.id}
                    key={index}
                    image={i.image}
                    description={i.description}
                    thumbnails={i.thumbnails}
                    updatedAt={i.updated_at}
                    isMandatoryImage={statusMandatoryUnitImage(i)}
                    onEditUnitImage={editUnitImage}
                    onDeleteImage={deleteUnitImage}
                  />
                )
              })}
            </tbody>
          </table>
          {filteredUnitImagesByPropertyId.length > 8 ? (
            <PaginationBlock
              count={filteredUnitImagesByPropertyId.length}
              page={page}
              onChangePage={setPage}
            />
          ) : null}
        </>
      ) : null}
      {!isLoading && filteredUnitImagesByPropertyId.length === 0 ? (
        <AlertUnitImagesEmpty/>
      ) : null}
      {isLoading ? (
        <LoadingBlock/>
      ) : null}
    </Box>
  );
}

export default ImagesPage;
